import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Avatar,
  Divider,
  useTheme,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Event as EventIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  BarChart as BarChartIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getOrganizerEvents } from '../services/eventService';
import { getImageUrl, getEventPlaceholderImage } from '../utils/imageUtils';

const MyEvents = () => {
  const theme = useTheme();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Fetch organizer's events
  const { data: events, isLoading, error } = useQuery('organizerEvents', getOrganizerEvents);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleMenuClick = (event, eventData) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedEvent(eventData);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleEditEvent = () => {
    history.push(`/event/${selectedEvent.event_id}`);
    handleMenuClose();
  };

  const handleDeleteEvent = () => {
    // Implement delete functionality
    handleMenuClose();
  };

  const handleUseAsTemplate = () => {
    if (selectedEvent) {
      history.push({
        pathname: '/create-event',
        state: { templateEvent: selectedEvent }
      });
    }
    handleMenuClose();
  };

  // Filter events based on search query and active tab
  const filteredEvents = useMemo(() => {
    if (!events) return [];
    
    let filtered = events.filter(event => 
      event.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.location?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Apply tab filter
    if (activeTab === 1) { // Upcoming
      filtered = filtered.filter(event => new Date(event.date) > new Date());
    } else if (activeTab === 2) { // Past
      filtered = filtered.filter(event => new Date(event.date) <= new Date());
    }
    // activeTab === 0 is "All Events", no additional filtering needed
    
    return filtered;
  }, [events, searchQuery, activeTab]);

  const stats = {
    totalEvents: events?.length || 0,
    upcomingEvents: events?.filter(event => new Date(event.date) > new Date()).length || 0,
    totalApplications: events?.reduce((sum, event) => sum + (event.application_count || 0), 0) || 0,
    activeEvents: events?.filter(event => new Date(event.date) > new Date()).length || 0,
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Error loading events: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pb: 4 }}>
        <Container maxWidth="xl" sx={{ pt: 4 }}>
          <Paper elevation={1} sx={{ p: 6, textAlign: 'center', bgcolor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
              No Events Found
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
              You haven't created any events yet. Create your first event to start connecting with vendors!
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => history.push('/create-event')}
              sx={{ borderRadius: 1 }}
            >
              Create Your First Event
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Modern Professional Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
            color: 'white',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' } }}>
                <Typography 
                  variant="h5" 
                  component="h1" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    mb: 1,
                    fontSize: { xs: '1.35rem', md: '1.5rem' },
                    lineHeight: 1.3
                  }}
                >
                  Event Management
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '0.875rem'
                  }}
                >
                  Manage your events and track vendor applications
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => history.push('/create-event')}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.25)',
                  }
                }}
              >
                Create Event
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Event Metrics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                      {stats.totalEvents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Total Events
                    </Typography>
                  </Box>
                  <EventIcon sx={{ fontSize: 36, color: alpha('#1976d2', 0.2) }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                      {stats.upcomingEvents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Upcoming
                    </Typography>
                  </Box>
                  <CalendarIcon sx={{ fontSize: 36, color: alpha('#2e7d32', 0.2) }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                      {stats.totalApplications}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Applications
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 36, color: alpha('#0288d1', 0.2) }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                      {stats.activeEvents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Active Now
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 36, color: alpha('#ed6c02', 0.2) }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filter Section */}
        <Paper elevation={1} sx={{ p: 2.5, mb: 3, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search events by name or location..."
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterIcon />}
                onClick={handleFilterClick}
                sx={{ borderRadius: 1 }}
              >
                Filter
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SortIcon />}
                onClick={handleSortClick}
                sx={{ borderRadius: 1 }}
              >
                Sort
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Main Content Tabs */}
        <Paper elevation={1} sx={{ mb: 3, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02)
            }}
          >
            <Tab label="All Events" />
            <Tab label="Upcoming" />
            <Tab label="Past Events" />
          </Tabs>

          {/* Events Grid */}
          <Box sx={{ p: 3, bgcolor: '#f5f7fa' }}>
            <Grid container spacing={3}>
              {filteredEvents.map((event) => (
                <Grid item xs={12} md={6} lg={4} key={event.event_id}>
                  <Card 
                    elevation={1}
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      cursor: 'pointer',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-4px)',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      history.push(`/event/${event.event_id}`);
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        paddingTop: '56.25%', // 16:9 aspect ratio
                        bgcolor: 'grey.200',
                      }}
                    >
                      <img
                        src={event.image_url ? getImageUrl(event.image_url) : getEventPlaceholderImage()}
                        alt={event.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.src = getEventPlaceholderImage();
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          display: 'flex',
                          gap: 1,
                        }}
                      >
                        <Chip
                          size="small"
                          label={new Date(event.date) > new Date() ? 'Upcoming' : 'Past'}
                          color={new Date(event.date) > new Date() ? 'success' : 'default'}
                          sx={{ fontWeight: 600, backdropFilter: 'blur(10px)', bgcolor: alpha('#fff', 0.9) }}
                        />
                      </Box>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }} noWrap>
                        {event.title}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(event.date), 'MMM d, yyyy • h:mm a')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {event.location}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.capacity || '∞'} spots
                          </Typography>
                        </Box>
                      </Box>
                      {event.category_id && (
                        <Box sx={{ mb: 1.5 }}>
                          <Chip
                            size="small"
                            label={categories.find(c => c.id === event.category_id)?.name || 'Uncategorized'}
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        </Box>
                      )}
                    </CardContent>
                    <Divider />
                    <CardActions sx={{ justifyContent: 'space-between', p: 2, bgcolor: alpha('#f5f5f5', 0.5) }} onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AnalyticsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {event.application_count || 0} applications
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Manage Applications">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              history.push(`/event/${event.event_id}/applications`);
                            }}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <BarChartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Event">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              history.push(`/event/${event.event_id}`);
                            }}
                            sx={{ 
                              color: 'text.secondary',
                              '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More Options">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuClick(e, event);
                            }}
                            sx={{ 
                              color: 'text.secondary',
                              '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) }
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
      </Paper>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem>All Events</MenuItem>
        <MenuItem>Upcoming Events</MenuItem>
        <MenuItem>Past Events</MenuItem>
        <MenuItem>Events with Applications</MenuItem>
      </Menu>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
      >
        <MenuItem>Date (Newest First)</MenuItem>
        <MenuItem>Date (Oldest First)</MenuItem>
        <MenuItem>Title (A-Z)</MenuItem>
        <MenuItem>Title (Z-A)</MenuItem>
        <MenuItem>Most Applications</MenuItem>
      </Menu>

      {/* Event Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditEvent}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Event</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUseAsTemplate}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Use as Template</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteEvent}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Event</ListItemText>
        </MenuItem>
      </Menu>
      </Container>
    </Box>
  );
};

// Add categories array
const categories = [
  { id: 1, name: 'Food & Beverage' },
  { id: 2, name: 'Entertainment' },
  { id: 3, name: 'Art & Crafts' },
  { id: 4, name: 'Technology' },
  { id: 5, name: 'Fashion' },
];

export default MyEvents; 